<template>
  <div class="actions-container">
    <div class="action-title">TOPS2 Actions</div>
    <div class="actions-grid">
      <div 
        v-for="action in actions" 
        :key="action.label"
        class="action-tile"
        :class="{ disabled: action.isDisabled }"
        @click="openDialog(action)"
      >
        <div class="action-icon">
          <component 
            v-if="!action.svgName" 
            :is="action.icon" 
            :size="24"
            :class="{ disabled: action.isDisabled }"
          />
          <v-img 
            v-else 
            :src="action.svgName" 
            width="24" 
            height="24"
            :alt="action.label"
            :class="{ disabled: action.isDisabled }"
          />
        </div>
        <div class="action-label">{{ action.label }}</div>
        
        <!-- Tooltip for Upload POD when disabled -->
        <div v-if="action.actionType === 'uploadPOD' && action.isDisabled" class="tooltip">
          {{ uploadPODTooltipText }}
        </div>
      </div>
    </div>
    
    <div v-if="quickActionMessage" class="action-message">
      {{ quickActionMessage }}
    </div>
  </div>
  <div class="actions-container">
    <div class="action-title">Assignment Communications</div>
    <div class="actions-grid">
      <div 
        v-for="action in assignmentCommunicationActions" 
        :key="action.label"
        class="action-tile"
        :class="{ disabled: action.isDisabled }"
        @click="openDialog(action)"
      >
        <div class="action-icon">
          <component 
            v-if="!action.svgName" 
            :is="action.icon" 
            :size="24"
            :class="{ disabled: action.isDisabled }"
          />
          <v-img 
            v-else 
            :src="action.svgName" 
            width="24" 
            height="24"
            :alt="action.label"
            :class="{ disabled: action.isDisabled }"
          />
        </div>
        <div class="action-label">{{ action.label }}</div>

        <!-- Tooltip for disabled actions with tooltip text -->
        <div v-if="action.isDisabled && getActionTooltip(action)!=''" class="tooltip">
          {{ action.tooltip }}
        </div>
      </div>
    </div>
  </div>
  
  <QuickActionDialog 
    v-if="isDialogOpen" 
    :key="currentActionType" 
    :isOpen="isDialogOpen" 
    :actionType="currentActionType"
    :actionData="actionData" 
    @close="closeDialog" 
    @send="handleAction" 
    @showNotification="showNotification"
    @followUpSent="handleCfuSent"
  />
  <Notification />
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { Users, FileUser } from 'lucide-vue-next'
import { Box } from 'lucide-vue-next'
import { Share2 } from 'lucide-vue-next'
import { FileCheck } from 'lucide-vue-next'
import { Clock } from 'lucide-vue-next'
import { ThumbsUp, CalendarDays, Award } from 'lucide-vue-next'
import QuickActionDialog from './QuickActionDialog.vue'
import Notification from "../commons/Notification.vue";
import nodeSvg from '../../../assets/node.png';
import taskSvg from '../../../assets/task.png';
import clientPng from '../../../assets/client.png';
import DatadogService from '../../service/DatadogService.js';
import { fetchClientFollowUpRecentActionIds } from '../../service/engagement.js';
import { EventBus } from '../../service/event-bus.js';
import personCheck from '../../../assets/person-check.png';
import routeToAdmin from '../../../assets/routeToAdmin.png';
import moment from 'moment';
export default {
  name: "QuickActions",
  components: {
    Users,
    FileUser,
    Box,
    Share2,
    FileCheck,
    Clock,
    ThumbsUp,
    CalendarDays,
    Award,
    QuickActionDialog,
    Notification,
  },
  data() {
    return {
      isDialogOpen: false,
      currentActionType: "",
      actionData: {},
      // Add reactive data for tracking folder changes
      currentFolderInfo: {
        isSentItems: false,
        folderName: '',
        lastUpdated: Date.now()
      },
      cfuAlreadySentIds: null,
      cfuManuallyDisabled: false,
    };
  },
  computed: {
    ...mapState(["quickActionMessage"]),
    ...mapGetters({
      selectedAssignment: "assignments/selectedAssignment", // Use the getter for selected assignment
      assignmentChecklist: "assignments/assignmentChecklist", // Use the getter for assignment checklist
      currentEmail: "email/currentEmail",
      isEligibleForPropertyFollowUp: "assignments/isEligibleForPropertyFollowUp",
      propertyFollowUpIneligibilityReason: "assignments/propertyFollowUpIneligibilityReason",
      loggedInUserEmail: "email/getLoggedInUserEmailAddress",
      userDetails: "email/getUserDetails",
    }),

    actions() {
      return [
        {
          label: "Save to Box",
          icon: "Box",
          actionType: "saveToBox",
          isDisabled: this.selectedAssignment == null || !this.currentEmail?.from,
        },
        { label: "Create New Quote", svgName: nodeSvg, actionType: "sendQuote", isDisabled: !this.currentEmail?.from },
        {
          label: "Upload POD",
          svgName: taskSvg,
          actionType: "uploadPOD",
          isDisabled: this.isUploadPODDisabled() || !this.currentEmail?.from,
          tooltip: this.uploadPODTooltipText,
        },
        {
          label: 'Route to Admin',
          svgName: routeToAdmin,
          actionType: 'sendAdminRequest',
          isDisabled: this.selectedAssignment == null || !this.currentEmail?.from,
        },
        {
          label: "Change Due Date",
          icon: "CalendarDays",
          actionType: "changeDueDate",
          isDisabled: this.selectedAssignment == null || !this.currentEmail?.from
        },
        // { label: 'Win/Loss', icon: 'Award', actionType: 'winOrLoss' }
        {
          label: "Forward to Appraiser",
          svgName: personCheck,
          actionType: "forwardToAppraiser",
          isDisabled: this.selectedAssignment == null || !this.currentEmail?.from,
        }
      ];
    },

    assignmentCommunicationActions() {
      const engagementType = (this.selectedAssignment?.engagementType ?? '').replace(/[^a-zA-Z]/g, '').toLowerCase();
      const isSingleOrMulti = engagementType.startsWith('single') || engagementType.startsWith('multi');
      const primaryValuerEmail = this.selectedAssignment?.primaryValuer?.emailAddress
      const status = this.selectedAssignment?.trackerEngagementStatus?.id
      const assignmentStatus = this.selectedAssignment?.assignmentStatus?.id
      return [
        {
          label: "Property Contact Request",
          svgName: clientPng,
          actionType: "clientCommunication",
          isDisabled: this.selectedAssignment == null || !isSingleOrMulti || primaryValuerEmail == null || status != 3,
        },
        {
          label: "Property Contact Follow Up",
          svgName: clientPng,
          actionType: "propertyFollowUp",
          isDisabled: !this.isSentItemsFolder()
            || (this.selectedAssignment != null && !isSingleOrMulti)
            || (this.selectedAssignment != null && this.isPFUEligibleForTile === false),
        },
        {
          label: "Client Follow Up",
          svgName: clientPng,
          actionType: "clientFollowUp",
          isDisabled: this.selectedAssignment != null && this.cfuDisabledReason !== null,
        },
        {
          label: "Client Introduction",
          svgName: clientPng,
          actionType: "clientIntroduction",
          isDisabled: this.selectedAssignment == null || !isSingleOrMulti || primaryValuerEmail == null || status != 3,
        }
      ]
    },

    // Date range used for CFU eligibility checks (last 20 days)
    cfuDateRange() {
      return {
        dateStart: moment().subtract(20, 'days').toDate(),
        dateEnd: moment().toDate(),
      };
    },
    // null = IDs not yet loaded (skip disable); false = explicitly ineligible
    isPFUEligibleForTile() {
      return this.isEligibleForPropertyFollowUp; // true | false | null
    },
    pfuIneligibilityReason() {
      return this.propertyFollowUpIneligibilityReason || 'This assignment is not eligible for Property Contact Follow Up.';
    },

    // Returns the first failing reason for Client Follow Up, or null if fully eligible
    cfuDisabledReason() {
      const engagementType = (this.selectedAssignment?.engagementType ?? '').replace(/[^a-zA-Z]/g, '').toLowerCase();
      const isSingleOrMulti = engagementType.startsWith('single') || engagementType.startsWith('multi');
      if (!isSingleOrMulti) return 'Disabled for Fund/Portfolio';

      // Condition 1: assignment must be booked (status 9)
      if (this.selectedAssignment?.assignmentStatus?.id !== 9) {
        return 'Selected assignment is not booked';
      }

      // Condition 2: logged-in user must be the Job Originator
      const originatorEmail = (this.selectedAssignment?.originatorEmail ?? '').toLowerCase();
      const userEmail = (this.userDetails?.emailAddress ?? '').toLowerCase();
      if (!originatorEmail || !userEmail || originatorEmail !== userEmail) {
        return 'User is not job originator of selected assignment';
      }

      // Condition 3: CFU must not have already been sent (based on backend check of recent actions)
      console.log('[QuickActions] cfuAlreadySentIds:', this.cfuAlreadySentIds);
      if (this.cfuManuallyDisabled) {
        return 'Follow-up has already been created';
      } else if (this.cfuAlreadySentIds !== null) {
        const assignmentId = String(this.selectedAssignment?.id || '');
        const isFollowupAlreadySent = this.cfuAlreadySentIds.some(id => String(id) === assignmentId);
        if (isFollowupAlreadySent) return 'Follow-up has already been created';
      }

      return null;
    },

    // Computed property for Upload POD tooltip text that updates reactively
    uploadPODTooltipText() {
      // Force reactivity by referencing currentFolderInfo.lastUpdated
      this.currentFolderInfo.lastUpdated;

      if (!this.isSentItemsFolder()) {
        return 'Upload POD is only available for sent emails';
      }

      if (!this.selectedAssignment) {
        return 'Please select an assignment to upload POD';
      }

      if (!this.assignmentChecklist) {
        return 'Assignment checklist not available';
      }

      const missingItems = [];
      if (!this.assignmentChecklist.servicesProvided) {
        missingItems.push('Services Provided');
      }
      if (!this.assignmentChecklist.valueConclusionsComplete) {
        missingItems.push('Value Conclusions Complete');
      }
      if (!this.assignmentChecklist.reviewComplete) {
        missingItems.push('Review Complete');
      }

      if (missingItems.length > 0) {
        return `Please complete prior checklist tasks before uploading POD`;
      }

      return 'Upload POD is not available';
    },
  },
  watch: {
    // Watch for changes in folder info to update tooltip
    'currentFolderInfo.isSentItems'(newVal, oldVal) {
      if (newVal !== oldVal) {
        console.log(`Folder context changed: ${oldVal} -> ${newVal}`);
        // Force tooltip to update by updating lastUpdated
        this.currentFolderInfo.lastUpdated = Date.now();
      }
    },
    selectedAssignment: {
      async handler(newVal) {
        if (!newVal) {
          this.cfuAlreadySentIds = null;
          this.cfuManuallyDisabled = false;
          return;
        }
        this.cfuManuallyDisabled = false;
        const ids = [String(newVal.viewEventId || ''), String(newVal.id || '')].filter(Boolean);
        this.cfuAlreadySentIds = await fetchClientFollowUpRecentActionIds(ids);
      },
      immediate: true,
    },
  },
  mounted() {
    // Initialize folder tracking
    this.updateFolderInfo();

    // Set up periodic checks for folder changes
    this.folderCheckInterval = setInterval(() => {
      this.updateFolderInfo();
    }, 1000); // Check every second

    // Listen for CFU sent event fired from inside the Vuetify teleported dialog
    EventBus.on('cfu-follow-up-sent', this.handleCfuSent);
  },
  beforeDestroy() {
    if (this.folderCheckInterval) {
      clearInterval(this.folderCheckInterval);
    }
    EventBus.off('cfu-follow-up-sent', this.handleCfuSent);
  },
  methods: {
    ...mapActions({
      add: "notification/add",
      doQuickAction: "index/doQuickAction",
      setActiveAction: "ui/setActiveAction",
    }),
    openDialog(action) {
      if (action.isDisabled) {
        // Track disabled action attempts
        DatadogService.trackTOPS2Action('action_disabled_click', {
          action_label: action.label,
          action_type: action.actionType,
          reason: 'action_disabled'
        });
        return;
      }

      // Track action dialog opening
      DatadogService.trackTOPS2Action('action_dialog_open', {
        action_label: action.label,
        action_type: action.actionType
      });

      this.currentActionType = action.actionType;
      this.setActiveAction(action.actionType);
      this.isDialogOpen = true;
    },
    closeDialog() {
      // Track dialog close
      DatadogService.trackTOPS2Action('action_dialog_close', {
        action_type: this.currentActionType
      });

      this.isDialogOpen = false;
      this.currentActionType = "";
      this.setActiveAction(null);
    },
    handleCfuSent() {
      console.log('[QuickActions] followUpSent — disabling CFU tile');
      this.cfuManuallyDisabled = true;
    },
    handleAction(label) {
      // Track action execution
      DatadogService.trackTOPS2Action('action_execute', {
        action_label: label,
        action_type: this.currentActionType
      });

      this.doQuickAction(label);
    },
    showNotification(message) {
      this.add({
        type: message.type,
        message: message.message,
        timeout: 5,
      });
    },

    // Helper method to check if we're in sent items folder (clearer logic)
    isSentItemsFolder() {
      try {
        const item = Office.context.mailbox?.item;
        const userEmail = Office.context.mailbox?.userProfile?.emailAddress;

        if (!item || !userEmail) {
          return false;
        }

        // Check if the email is from the current user (indicates sent items)
        const isFromCurrentUser = (
          item &&
          item.itemType === Office.MailboxEnums.ItemType.Message &&
          item.from?.emailAddress.toLowerCase() === userEmail.toLowerCase()
        );

        return isFromCurrentUser;
      } catch (error) {
        console.error('Error checking sent items folder:', error);
        return false;
      }
    },

    // Helper method to determine if Upload POD should be disabled
    isUploadPODDisabled() {
      // First check: Only enable for sent items folder
      if (!this.isSentItemsFolder()) {
        return true;
      }

      // Second check: Assignment must be selected
      if (!this.selectedAssignment) {
        return true;
      }

      // Third check: Assignment checklist requirements
      if (!this.assignmentChecklist) {
        return true;
      }
      console.log(this.assignmentChecklist.servicesProvided, this.assignmentChecklist.valueConclusionsComplete, this.assignmentChecklist.reviewComplete);

      // All checklist items must be completed
      return (
        !this.assignmentChecklist.servicesProvided ||
        !this.assignmentChecklist.valueConclusionsComplete ||
        !this.assignmentChecklist.reviewComplete
      );
    },

    // Get tooltip text explaining why Upload POD is disabled
    getUploadPODTooltipText() {
      if (!this.isSentItemsFolder()) {
        return 'Upload POD is only available for sent emails';
      }

      if (!this.selectedAssignment) {
        return 'Please select an assignment to upload POD';
      }

      if (!this.assignmentChecklist) {
        return 'Assignment checklist not available';
      }

      const missingItems = [];
      if (!this.assignmentChecklist.servicesProvided) {
        missingItems.push('Services Provided');
      }
      if (!this.assignmentChecklist.valueConclusionsComplete) {
        missingItems.push('Value Conclusions Complete');
      }
      if (!this.assignmentChecklist.reviewComplete) {
        missingItems.push('Review Complete');
      }

      if (missingItems.length > 0) {
        return `Please complete prior checklist tasks before uploading POD`;
      }

      return 'Upload POD is not available';
    },

    // Method to update folder information reactively
    updateFolderInfo() {
      try {
        const isSentItems = this.isSentItemsFolder();
        const currentTime = Date.now();

        // Only update if there's a change to avoid unnecessary re-renders
        if (this.currentFolderInfo.isSentItems !== isSentItems) {
          this.currentFolderInfo.isSentItems = isSentItems;
          this.currentFolderInfo.lastUpdated = currentTime;

          console.log(`Folder changed - isSentItems: ${isSentItems}`);
        }
      } catch (error) {
        console.error('Error updating folder info:', error);
      }
    },

    // Method to manually trigger folder info update (can be called externally)
    refreshFolderContext() {
      this.updateFolderInfo();
      // Force a re-evaluation of the tooltip by updating the timestamp
      this.$nextTick(() => {
        this.currentFolderInfo.lastUpdated = Date.now();
      });
    },

    // Tooltip for disabled actions with tooltip text
    getActionTooltip(action) {
      action.tooltip = ''; // Default to no tooltip
      // Handle communication action tooltips
      const communicationActionTypes = ['clientCommunication', 'propertyFollowUp', 'clientFollowUp', 'clientIntroduction'];
      if (communicationActionTypes.includes(action.actionType)) {
        if (action.actionType === 'propertyFollowUp' && !this.isSentItemsFolder()) {
          action.tooltip = 'This action is only available for sent emails';
        } else if (action.actionType === 'propertyFollowUp' && this.selectedAssignment != null && this.isPFUEligibleForTile === false) {
          // Eligibility-based disable for Property Contact Follow Up
          action.tooltip = this.pfuIneligibilityReason;
        } else if (action.actionType === 'clientFollowUp' && this.selectedAssignment != null) {
          // Eligibility-based disable for Client Follow Up (booked / JO / already sent)
          action.tooltip = this.cfuDisabledReason;
        } else if (this.selectedAssignment != null) {
          const primaryValuerEmail = this.selectedAssignment?.primaryValuer?.emailAddress;
          const status = this.selectedAssignment?.trackerEngagementStatus?.id;
          action.tooltip = 'Disabled for Fund/Portfolio';
          if (status != 3) {
            action.tooltip = 'This action is only available for assignments that are In Progress';
          }
          if (primaryValuerEmail == null) {
            action.tooltip = 'This action is only available for assignments with a Primary Valuer assigned';
          }
        }
      }
      return action.tooltip;
    },
  },
};
</script>
<style scoped>
.actions-container {
  width: 100%;
  margin-bottom: 24px;
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  width: 100%;
  max-width: min(480px, 100%);
  margin: 0 auto;
  overflow: visible; /* allow tooltips to escape grid bounds */
}

/* For wider screens, allow the grid to expand more */
@media (min-width: 600px) {
  .actions-grid {
    max-width: 100%;
    gap: 20px;
  }
}

.action-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px 8px;
  min-height: 80px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
}

.action-tile:hover:not(.disabled) {
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.15);
  border-color: #ccc;
}

.action-tile:active:not(.disabled) {
  transform: translateY(1px);
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.1);
}

.action-tile.disabled {
  cursor: not-allowed;
  background: #f8f9fa;
}

.action-icon {
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-icon.disabled,
.action-icon .disabled {
  opacity: 0.5;
  filter: grayscale(100%);
}

.action-label {
  font-family: 'Calibre-R', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
  text-align: center;
  color: #1a1a1a;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  max-width: 100%;
}

.action-tile.disabled .action-label {
  opacity: 0.5;
  color: #666;
}

.action-message {
  margin-top: 16px;
  color: #006341;
  font-weight: 500;
  text-align: center;
  font-size: 14px;
}

/* Tooltip styles */
.tooltip {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  bottom: 110%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #333;
  color: #ffffff;
  text-align: center;
  border-radius: 6px;
  padding: 8px 12px;
  z-index: 1000;
  font-size: 12px;
  line-height: 1.4;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  white-space: normal;
  max-width: 250px;
  width: max-content;
  transition: opacity 0.2s ease, visibility 0.2s ease;
}

.action-tile.disabled:hover .tooltip {
  visibility: visible;
  opacity: 1;
}

.tooltip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #333 transparent transparent transparent;
}

/*
 * Column-aware tooltip positioning.
 * 3-column grid: col 1 = 3n+1, col 2 = 3n+2, col 3 = 3n
 * Left column  → pin tooltip to left edge of tile, caret near left
 * Right column → pin tooltip to right edge of tile, caret near right
 * Middle column stays centered (default)
 */

/* Left column */
.action-tile:nth-child(3n+1) .tooltip {
  left: 0;
  right: auto;
  transform: none;
}
.action-tile:nth-child(3n+1) .tooltip::after {
  left: 16px;
  margin-left: 0;
}

/* Right column */
.action-tile:nth-child(3n) .tooltip {
  left: auto;
  right: 0;
  transform: none;
}
.action-tile:nth-child(3n) .tooltip::after {
  left: auto;
  right: 11px;
  margin-left: 0;
}

/* Responsive Design */
@media (max-width: 480px) {
  .actions-grid {
    gap: 12px;
    max-width: 100%;
  }
  
  .action-tile {
    min-height: 70px;
    padding: 12px 6px;
  }
  
  .action-label {
    font-size: 11px;
  }
  
  .action-icon svg,
  .action-icon img {
    width: 20px !important;
    height: 20px !important;
  }
}

@media (max-width: 360px) {
  .actions-grid {
    gap: 8px;
  }
  
  .action-tile {
    min-height: 65px;
    padding: 10px 4px;
  }
  
  .action-label {
    font-size: 10px;
  }
  
  .action-icon svg,
  .action-icon img {
    width: 18px !important;
    height: 18px !important;
  }
}

@media (max-width: 320px) {
  .actions-grid {
    gap: 6px;
  }
  
  .action-tile {
    min-height: 60px;
    padding: 8px 4px;
    border-radius: 6px;
  }
  
  .action-label {
    font-size: 9px;
    line-height: 1.2;
  }
  
  .action-icon {
    margin-bottom: 6px;
  }
  
  .action-icon svg,
  .action-icon img {
    width: 16px !important;
    height: 16px !important;
  }
  
  .tooltip {
    bottom: 120%;
    font-size: 11px;
    padding: 6px 8px;
    max-width: 200px;
  }
}

.action-title {
  margin-bottom: 10px;
  font-size: 16px;
  font-family:'Calibre-R', sans-serif;
}
</style>
